import React, { useMemo, useState } from "react";
import { Button, Card, Empty, Input, Modal, Select, Space, Tag, Typography } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BracketView, EntrantStatusList, MatchCard, OrganizerWizard, ResultSheet, StandingsTable, TournamentHeader, UpNextList } from "@dowty/tournament-ui";
import type { Match, Tournament, TournamentSettings, OrganizerWizardValue } from "@dowty/tournament-ui";
import "@dowty/tournament-ui/styles.css";
import "./tournament.css";
import { ErrorState, LoadingState } from "@dowty/ui";
import { useAuth } from "../context/AuthContext";
import { fetchActiveEvent } from "../services/drinks/EventService";
import { useLiveTournament } from "./useLiveTournament";
import { useTranslation } from "react-i18next";
import { advanceTournament, changeEntrantStatus, createTournament, joinTournament, reportResult, setShareToken } from "./client";
import type { TournamentVisibility } from "./client";
import { useAppFeedback } from "../components/useAppFeedback";

const defaultSettings = (): TournamentSettings => ({
    scoring: "winner_only", allowDraws: false, resultEntry: "participants",
    tieBreakOrder: ["points", "headToHead", "scoreDifference"], seedMode: "random", tables: null,
});

const initialWizard = (): OrganizerWizardValue => ({
    name: "", discipline: "beerpong", entrants: [{ name: "", seed: 1 }, { name: "", seed: 2 }],
    format: "single_elim", groupCount: 4, advanceCount: 2, settings: defaultSettings(),
});

const formats = new Set(["single_elim", "double_elim", "round_robin", "groups_knockout"]);

export const TournamentWizardPage: React.FC = () => {
    const { message } = useAppFeedback();
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [value, setValue] = useState(initialWizard);
    const [visibility, setVisibility] = useState<TournamentVisibility>("public");
    const [password, setPassword] = useState("");
    const [contextId, setContextId] = useState<number | null>(() => {
        const parsed = Number(searchParams.get("eventId"));
        return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
    });
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (contextId !== null || !user) return;
        void fetchActiveEvent(user).then((event) => setContextId(event?.eventId ?? null)).catch(() => setContextId(null));
    }, [contextId, user]);

    const submit = async (form: OrganizerWizardValue) => {
        if (saving) return;
        if (!user || contextId === null) {
            message.error(t("tournaments.signInToCreate"));
            return;
        }
        if (!form.name.trim() || form.entrants.length < 2 || form.entrants.some((entrant) => !entrant.name.trim())) {
            message.error(t("tournaments.enterNameEntrants"));
            return;
        }
        if (visibility === "password" && !password.trim()) {
            message.error(t("tournaments.enterPassword"));
            return;
        }
        setSaving(true);
        try {
            const created = await createTournament(form, contextId, visibility, user, password);
            if (form.settings.shareLink?.trim()) {
                try {
                    await setShareToken(created, true, user);
                } catch {
                    navigate(`/tournaments/${created.id}`);
                    message.error(t("tournaments.shareLinkFailed"));
                    return;
                }
            }
            navigate(`/tournaments/${created.id}`);
        } catch {
            message.error(t("tournaments.createError"));
        } finally {
            setSaving(false);
        }
    };

    return <Space direction="vertical" size="large" className="tournament-page-content tournament-create">
        <TournamentHeader name={t("tournamentUi.createTitle")} status="Draft" format={contextId ? t("tournamentUi.event", { id: contextId }) : t("tournamentUi.chooseEvent")} labels={{ Draft: t("tournamentUi.status.draft") }} />
        {!user || contextId === null ? <Empty description={t("tournaments.signInEmpty")} /> : <>
            <label htmlFor="tournament-visibility">{t("tournaments.visibility.label")}</label>
            <Select id="tournament-visibility" value={visibility} onChange={setVisibility} options={[
                { value: "public", label: t("tournaments.visibility.public") }, { value: "friends", label: t("tournaments.visibility.friends") },
                { value: "password", label: t("tournaments.visibility.password") }, { value: "private", label: t("tournaments.visibility.private") },
            ]} />
            {visibility === "password" && <label>{t("tournaments.passwordLabel")}<Input.Password aria-label={t("tournaments.passwordAria")} value={password} onChange={(event) => setPassword(event.target.value)} maxLength={64} /></label>}
            <OrganizerWizard value={value} onChange={(next) => setValue({ ...next, format: formats.has(next.format) ? next.format : value.format })} onSubmit={submit} />
            {saving && <Typography.Text role="status">{t("tournaments.creating")}</Typography.Text>}
        </>}
    </Space>;
};

const TournamentViewPage: React.FC<{ player: boolean }> = ({ player }) => {
    const { message } = useAppFeedback();
    const { t } = useTranslation();
    const { id: rawId } = useParams<{ id: string }>();
    const { user } = useAuth();
    const id = Number(rawId);
    const tournamentId = Number.isSafeInteger(id) && id > 0 ? id : null;
    const { view, loading, error, stale, updatedAt, refresh } = useLiveTournament(tournamentId, user);
    const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
    const [saving, setSaving] = useState(false);
    const [joinPassword, setJoinPassword] = useState("");

    const withSaving = async (action: () => Promise<void>, onError: (error: unknown) => void) => {
        if (saving) return;
        setSaving(true);
        try { await action(); }
        catch (error) { onError(error); }
        finally { setSaving(false); }
    };

    const canReport = (match: Match) => Boolean(view && (view.me.canOrganize || view.me.canReportMatchIds.includes(match.id)));
    const nextMatch = useMemo(() => view?.matches
        .filter((match) => view.me.canReportMatchIds.includes(match.id) && match.status !== "Done" && match.status !== "Bye")
        .sort((a, b) => a.round - b.round || a.position - b.position)[0] ?? null, [view]);

    const saveResult = async (match: Match, result: Parameters<typeof reportResult>[2]) => {
        if (!view || !user || !canReport(match)) return;
        await withSaving(async () => {
            await reportResult(view, match, result, user, match.status === "Done");
            setSelectedMatch(null);
            await refresh();
        }, () => message.error(t("tournaments.saveResultError")));
    };

    const updateEntrant = async (entrantId: number, reinstate: boolean) => {
        if (!view || !user) return;
        await withSaving(async () => {
            await changeEntrantStatus(view, entrantId, user, reinstate);
            await refresh();
        }, () => message.error(t("tournaments.updateEntrantError")));
    };

    if (!user) return <div className="tournament-page-content"><Empty description={t("tournaments.signInToOpen")} /></div>;
    if (loading && !view) return <div className="tournament-page-content"><LoadingState variant="list" label={t("tournaments.loadingOne")} /></div>;
    if (error && !view) return <div className="tournament-page-content"><ErrorState message={error} retry={() => { void refresh(); }} retryLabel={t("tournaments.retry")} /></div>;
    if (!view) return <div className="tournament-page-content"><Empty description={t("tournaments.notFound")} /></div>;

    const showPlayerView = player || !view.me.canOrganize;
    const ownNextMatch = showPlayerView && nextMatch;
    const hasBracket = view.stages.some((stage) =>
        (stage.format === "single_elim" || stage.format === "double_elim") &&
        view.matches.some((match) => match.stageId === stage.id)
    );
    return <Space direction="vertical" size="large" className="tournament-page-content tournament-detail">
        <TournamentHeader name={view.name} status={view.status} format={view.discipline} labels={{ Draft: t("tournamentUi.status.draft"), Running: t("tournamentUi.status.running"), Completed: t("tournamentUi.status.completed"), Cancelled: t("tournamentUi.status.cancelled") }} actions={<Space wrap>
            <Typography.Text>{t(stale ? "tournaments.liveStaleUpdated" : "tournaments.liveUpdated", { seconds: Math.max(0, Math.floor((Date.now() - (updatedAt ?? Date.now())) / 1000)) })}</Typography.Text>
            {view.me.canOrganize && !showPlayerView && <a href={`/tournaments/${view.id}/player`}>{t("tournaments.playerView")}</a>}
            {player && view.me.canOrganize && <a href={`/tournaments/${view.id}`}>{t("tournaments.organizerView")}</a>}
        </Space>} />
        {error && <Typography.Text type="warning">{t("tournaments.liveUnavailable")}</Typography.Text>}
        {showPlayerView && ownNextMatch && <Card title={t("tournaments.yourNext")}><MatchCard match={ownNextMatch} entrants={view.entrants} labels={{ pass: t("tournamentUi.pass"), passHint: t("tournamentUi.passHint") }} canReport={canReport(ownNextMatch)} onReport={setSelectedMatch} /></Card>}
        {showPlayerView && !nextMatch && <Card title={t("tournaments.yourNext")}><Empty description={t("tournaments.noUpcoming")} /></Card>}
        <Card><UpNextList tournament={view} canReport={canReport} onReport={setSelectedMatch} onSelect={setSelectedMatch} /></Card>
        {hasBracket && <Card title={t("tournaments.bracket")}>
        {view.entrants.some((entrant) => entrant.withdrawn) && <section className="tournament-eliminated" aria-label={t("tournamentUi.eliminatedEntrants")}>
            {view.entrants.map((entrant) => {
                if (!entrant.withdrawn) return null;
                const round = view.matches.reduce<number | null>((latest, match) => match.status === "Done" && match.winnerId !== entrant.id && (match.entrantAId === entrant.id || match.entrantBId === entrant.id)
                    ? Math.max(latest ?? 0, match.round) : latest, null);
                return <Tag key={entrant.id} color="default">{entrant.name}: {round ? t("tournamentUi.eliminatedRound", { round }) : t("tournamentUi.eliminated")}</Tag>;
            })}
        </section>}
        <div className="tournament-bracket-scroll">
            <Typography.Text type="secondary" className="tournament-bracket-hint">{t("tournamentUi.bracketScrollHint")}</Typography.Text>
            <BracketView tournament={view} canReport={canReport} onReport={setSelectedMatch} onSelect={setSelectedMatch} matchLabels={{ pass: t("tournamentUi.pass"), passHint: t("tournamentUi.passHint") }} />
        </div></Card>}
        {view.standings.length > 0 && <Card title={t("tournaments.standings")}><StandingsTable tournament={view} /></Card>}
        {!view.me.canOrganize && <>
            {view.visibility === "password" && <Input.Password aria-label={t("tournaments.passwordLabel")} value={joinPassword} onChange={(event) => setJoinPassword(event.target.value)} disabled={saving} maxLength={64} />}
            <Button type="primary" className="tournament-join" loading={saving} disabled={saving} onClick={() => void withSaving(async () => {
                await joinTournament(view.id, user, joinPassword);
                await refresh();
                setJoinPassword("");
            }, () => message.error(t("tournaments.joinError")))}>{t("tournaments.join")}</Button>
        </>}
        {view.me.canOrganize && <Card title={t("tournaments.entrants")}><ListEntrants tournament={view} onChange={updateEntrant} />
            <div className="tournament-detail-actions">
                <Button loading={saving} disabled={saving || view.status !== "Running"} onClick={() => void withSaving(async () => {
                    await advanceTournament(view, user);
                    await refresh();
                }, (error) => message.error(t(error instanceof Error && [
                    "resolve all winner bracket matches before advancing.",
                    "no bracket updates available. resolve remaining matches first.",
                ].includes(error.message.toLowerCase()) ? "tournaments.advancePendingMatches" : "tournaments.advanceError")))}>{t("tournaments.advance")}</Button>
                <Button loading={saving} disabled={saving} onClick={() => void withSaving(async () => {
                    await setShareToken(view, !view.settings.shareLink, user);
                    await refresh();
                }, () => message.error(t("tournaments.shareError")))}>{view.settings.shareLink ? t("tournaments.revokeShare") : t("tournaments.createShare")}</Button>
            </div>
            {view.settings.shareLink && <Typography.Text className="tournament-share-link" copyable>{`https://dowtex.com/t/${view.settings.shareLink}`}</Typography.Text>}
        </Card>}
        {selectedMatch && !saving && canReport(selectedMatch) && <ResultSheet key={selectedMatch.id} open match={selectedMatch} entrants={view.entrants} canReport={canReport(selectedMatch)} scoring={view.settings.scoring} bestOf={view.settings.bestOf} allowDraws={view.settings.allowDraws} onCancel={() => setSelectedMatch(null)} onConfirm={(match, result) => { void saveResult(match, result); }} />}
        {selectedMatch && !saving && !canReport(selectedMatch) && <Modal open title={t("tournamentUi.matchDetails")} footer={null} onCancel={() => setSelectedMatch(null)}>
            <MatchCard match={selectedMatch} entrants={view.entrants} readOnly />
        </Modal>}
    </Space>;
};


const ListEntrants: React.FC<{ tournament: Tournament; onChange: (id: number, reinstate: boolean) => void }> = ({ tournament, onChange }) => {
    const { t } = useTranslation();
    return <EntrantStatusList entrants={tournament.entrants} onEliminate={(entrant) => onChange(entrant.id, false)} onRestore={(entrant) => onChange(entrant.id, true)} labels={{ active: t("tournamentUi.entrants.active"), out: t("tournamentUi.entrants.out"), eliminate: t("tournamentUi.entrants.withdraw"), restore: t("tournamentUi.entrants.reinstate"), summary: (active, out) => t("tournamentUi.entrants.summary", { active, out }), empty: t("tournamentUi.entrants.empty") }} />;
};

export const OrganizerTournamentPage: React.FC = () => <TournamentViewPage player={false} />;
export const PlayerTournamentPage: React.FC = () => <TournamentViewPage player />;
